import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * SnapBackPill — tiny safety hatch that lets an admin instantly return to their
 * admin session after using DevSwitcher to log in as another user.
 *
 * Flow:
 *   1. On admin login, backend sets `admin_snapback_token` cookie (long-lived JWT).
 *   2. If the admin then swaps to a non-admin account via /dev/login-as, the cookie
 *      remains — but the DevSwitcher pill hides itself because the current session
 *      isn't admin.
 *   3. This pill probes GET /dev/snapback-status. If a valid cookie exists AND the
 *      current session isn't already that admin, we render a small floating pill.
 *   4. Tap → POST /dev/admin-snapback → refresh auth context → done.
 *
 * The pill lives in the TOP-LEFT so it doesn't collide with the DevSwitcher (bottom-left)
 * or the main nav. It renders nothing when snapback isn't available (prod, no cookie,
 * or already admin).
 */
export default function SnapBackPill() {
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [available, setAvailable] = useState(false);
    const [adminLabel, setAdminLabel] = useState("admin");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const probe = async () => {
            try {
                const { data } = await api.get("/dev/snapback-status");
                if (cancelled) return;
                setAvailable(!!data?.available);
                if (data?.admin?.name) setAdminLabel(data.admin.name);
                else if (data?.admin?.email) setAdminLabel(data.admin.email.split("@")[0]);
            } catch {
                if (!cancelled) setAvailable(false);
            }
        };
        probe();
        // Re-probe whenever the window regains focus — covers the case where the user
        // swaps accounts in a different tab and comes back.
        window.addEventListener("focus", probe);
        return () => { cancelled = true; window.removeEventListener("focus", probe); };
    }, []);

    const snap = async () => {
        setBusy(true);
        try {
            await api.post("/dev/admin-snapback");
            await refresh();
            toast.success(`Back as ${adminLabel}`);
            setAvailable(false);
            navigate("/dashboard");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    if (!available) return null;

    return (
        <button
            type="button"
            onClick={snap}
            disabled={busy}
            data-testid="snapback-admin-pill"
            title={`One-tap return to ${adminLabel}`}
            className="fixed top-20 left-4 z-[9998] inline-flex items-center gap-2 h-9 pl-3 pr-4 rounded-full bg-amber-500/95 text-ink text-xs font-black shadow-lg backdrop-blur-sm ring-1 ring-amber-700/30 hover:bg-amber-400 hover:-translate-y-0.5 transition-all disabled:opacity-70"
        >
            <ShieldCheck size={14} />
            {busy ? "Switching…" : <>Back to {adminLabel}</>}
        </button>
    );
}
